import { useState, useRef, useEffect, useMemo } from 'react';
import { Play, Pause, SkipBack, SkipForward, Volume2, VolumeX, Music } from 'lucide-react';
import { API } from '@/App';
import { readPlayerState, writePlayerState, markPlayerClosed } from '@/utils/playerState';
import { usePlayer } from '@/context/PlayerContext';
import WaveformBars from '@/components/WaveformBars';
import SafeImg from '@/components/SafeImg';

export default function AudioPlayer({ track, tracks = [], onClose, onTrackChange, onPlayingChange }) {
  const audioRef = useRef(null);
  const progressRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [duckFactor, setDuckFactor] = useState(1);

  // VoiceoverEngine fires this event to drop the music while it speaks.
  // We multiply the player's own volume by duckFactor so the user-set
  // volume remains the ceiling — ducking can only attenuate, never boost.
  useEffect(() => {
    const onDuck = (e) => {
      const f = Number(e?.detail?.factor);
      if (Number.isFinite(f) && f >= 0 && f <= 1) setDuckFactor(f);
    };
    window.addEventListener('cratelab:duck', onDuck);
    return () => window.removeEventListener('cratelab:duck', onDuck);
  }, []);
  const [isMuted, setIsMuted] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [isSeeking, setIsSeeking] = useState(false);
  const [audioSrc, setAudioSrc] = useState('');
  const { registerAudioElement, prefetchNextAudioUrl } = usePlayer();
  const prevTrackIdRef = useRef(null);

  // Expose the <audio> element to PlayerContext so pages can call play()
  // synchronously inside their click handlers (required by iOS Safari to
  // grant background-audio rights).
  useEffect(() => {
    if (registerAudioElement) registerAudioElement(audioRef.current);
    return () => { if (registerAudioElement) registerAudioElement(null); };
  }, [registerAudioElement]);

  // Audio src is set IMPERATIVELY by PlayerContext (it owns the fetch +
  // src + play() flow inside the user-gesture chain). This component
  // never sets src or fetches the audio URL — doing so would race with
  // PlayerContext and cause the audio element to reset to paused state
  // mid-load.
  useEffect(() => {
    if (!track?.id) {
      if (audioRef.current) audioRef.current.removeAttribute('src');
      setAudioSrc('');
    }
  }, [track?.id]);

  // Mirror the audio element's actual src into React state so the
  // "seek to saved position" effect below has something to depend on.
  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const sync = () => setAudioSrc(audio.src || '');
    audio.addEventListener('loadstart', sync);
    audio.addEventListener('canplay', sync);
    return () => {
      audio.removeEventListener('loadstart', sync);
      audio.removeEventListener('canplay', sync);
    };
  }, []);

  // Get current track index in the list
  const currentIndex = tracks.findIndex(t => t.id === track?.id);
  const hasNext = currentIndex < tracks.length - 1 && currentIndex !== -1;
  const hasPrev = currentIndex > 0;

  // Stable refs so the MediaSession action handlers (set ONCE on mount)
  // can always read the latest values without being re-registered. Re-
  // registering them on every render is what previously caused iOS to
  // revoke background-audio rights mid-playback and instantly stop the
  // audio whenever the screen locked.
  const tracksRef = useRef(tracks);
  const currentIndexRef = useRef(currentIndex);
  const hasNextRef = useRef(hasNext);
  const hasPrevRef = useRef(hasPrev);
  const onTrackChangeRef = useRef(onTrackChange);
  const isLoadedRef = useRef(isLoaded);
  useEffect(() => { tracksRef.current = tracks; }, [tracks]);
  useEffect(() => { currentIndexRef.current = currentIndex; }, [currentIndex]);
  useEffect(() => { hasNextRef.current = hasNext; }, [hasNext]);
  useEffect(() => { hasPrevRef.current = hasPrev; }, [hasPrev]);
  useEffect(() => { onTrackChangeRef.current = onTrackChange; }, [onTrackChange]);
  useEffect(() => { isLoadedRef.current = isLoaded; }, [isLoaded]);

  // Register MediaSession action handlers ONCE — never null them. iOS
  // Safari uses the handler list to decide whether the page owns "media
  // session"; if we null them mid-playback (which previous code did on
  // every dependency change), iOS revokes background-audio permission and
  // pauses the track the moment the screen locks.
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;

    const ms = navigator.mediaSession;

    ms.setActionHandler('play', () => {
      const a = audioRef.current;
      if (!a) return;
      a.play().catch(() => {});
      setIsPlaying(true);
    });

    ms.setActionHandler('pause', () => {
      const a = audioRef.current;
      if (!a) return;
      a.pause();
      setIsPlaying(false);
    });

    ms.setActionHandler('previoustrack', () => {
      if (hasPrevRef.current && onTrackChangeRef.current) {
        onTrackChangeRef.current(tracksRef.current[currentIndexRef.current - 1]);
      }
    });

    ms.setActionHandler('nexttrack', () => {
      if (hasNextRef.current && onTrackChangeRef.current) {
        onTrackChangeRef.current(tracksRef.current[currentIndexRef.current + 1]);
      }
    });

    // Suppress the ±10s seek buttons on iOS lock screen — we want
    // Prev/Next to be the visible controls (the DJ workflow).
    try { ms.setActionHandler('seekbackward', null); } catch { /* ignore */ }
    try { ms.setActionHandler('seekforward', null); } catch { /* ignore */ }

    ms.setActionHandler('seekto', (details) => {
      const a = audioRef.current;
      if (!a || !isLoadedRef.current) return;
      if (details.seekTime !== undefined) {
        a.currentTime = details.seekTime;
        setCurrentTime(details.seekTime);
      }
    });

    ms.setActionHandler('stop', () => {
      const a = audioRef.current;
      if (!a) return;
      a.pause();
      a.currentTime = 0;
      setIsPlaying(false);
      setCurrentTime(0);
    });

    // No cleanup that nulls handlers — they should live for the lifetime
    // of the AudioPlayer (mounted globally now).
  }, []);

  // Update MediaSession metadata when the active track changes. Kept in a
  // separate effect (depends only on track.id) so it doesn't re-register
  // action handlers.
  useEffect(() => {
    if (!('mediaSession' in navigator) || !track) return;
    const defaultArtwork = [
      {
        src: 'data:image/svg+xml,' + encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512"><rect fill="#1a0000" width="512" height="512"/><text x="256" y="280" font-family="Arial" font-size="200" fill="#ff003c" text-anchor="middle">♪</text></svg>'),
        sizes: '512x512',
        type: 'image/svg+xml',
      },
    ];
    navigator.mediaSession.metadata = new window.MediaMetadata({
      title: track.title || 'Unknown Track',
      artist: track.artist || 'Unknown Artist',
      album: 'CRATELAB',
      artwork: defaultArtwork,
    });
  }, [track?.id, track?.title, track?.artist]);

  // iOS Safari bfcache + pagehide hardening
  // ----------------------------------------
  // When the user locks their phone, iOS suspends the page. On unlock the
  // browser dispatches `pageshow` with `event.persisted === true` if the
  // page was restored from the back-forward cache. In that scenario the
  // <audio> element can be in a confused state — paused with src intact but
  // currentTime stuck. We force the saved src + position back in and try
  // to resume playback.
  //
  // We also listen for `visibilitychange`; on rare iOS builds the audio
  // pauses silently when the screen turns off and never reports a `pause`
  // event, so we re-issue play() when the tab becomes visible again.
  //
  // IMPORTANT: only auto-resume if the user was ACTIVELY playing when the
  // page was hidden. Otherwise every tab-switch → tab-return would revive
  // an intentionally-paused track.
  const wasPlayingWhenHiddenRef = useRef(false);
  useEffect(() => {
    const handlePageShow = () => {
      const audio = audioRef.current;
      if (!audio || !track?.id) return;
      // Only restore from a persisted-close-and-reopen if we were playing.
      if (!wasPlayingWhenHiddenRef.current) return;
      if (audio.paused) {
        try {
          const saved = readPlayerState();
          if (saved && saved.trackId === track.id && saved.currentTime > 1) {
            audio.currentTime = saved.currentTime;
          }
        } catch { /* ignore */ }
        audio.play().catch(() => { /* user-gesture required, will recover on tap */ });
      }
    };

    const handleVisibility = () => {
      const audio = audioRef.current;
      if (!audio || !track?.id) return;

      if (document.visibilityState === 'hidden') {
        // Remember whether the user had audio actively playing.
        wasPlayingWhenHiddenRef.current = !audio.paused;
        return;
      }

      // visibilityState === 'visible' — only resume if we WERE playing
      // when hidden AND we're not still playing (iOS quietly paused us).
      if (!wasPlayingWhenHiddenRef.current) return;
      const saved = readPlayerState();
      if (!saved || saved.closed) return;
      if (audio.paused) {
        if (saved.trackId === track.id && saved.currentTime > 1 && Math.abs(audio.currentTime - saved.currentTime) > 2) {
          try { audio.currentTime = saved.currentTime; } catch { /* ignore */ }
        }
        audio.play().catch(() => { /* user-gesture required, will recover on tap */ });
      }
    };

    window.addEventListener('pageshow', handlePageShow);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('pageshow', handlePageShow);
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [track?.id]);

  // Update Media Session playback state and position
  useEffect(() => {
    if (!('mediaSession' in navigator)) return;

    // CRITICAL for iOS 26 Safari: keep playbackState = 'playing' as long
    // as the user has the player open and a track loaded. iOS 26 Safari
    // briefly pauses the audio element during the lock-screen transition
    // (a 100-200ms blip) — if we sync playbackState to 'paused' during
    // that blip, iOS interprets the session as ended and revokes
    // background-audio rights, killing the audio entirely.
    //
    // We only mark playbackState as 'paused' if the user actually
    // toggled pause via the in-app button (tracked via a brief state
    // window). Transient browser-side pauses during phone-lock or
    // visibility transitions are ignored.
    if (track?.id) {
      navigator.mediaSession.playbackState = 'playing';
    } else {
      navigator.mediaSession.playbackState = 'none';
    }

    // Update position state for lock screen progress bar
    if (isLoaded && duration > 0) {
      try {
        navigator.mediaSession.setPositionState({
          duration: duration,
          playbackRate: 1,
          position: currentTime
        });
      } catch (e) {
        // Some browsers don't support setPositionState
      }
    }
  }, [currentTime, duration, isLoaded, track?.id]);

  // Notify parent of playing state changes
  useEffect(() => {
    if (onPlayingChange) {
      onPlayingChange(isPlaying);
    }
  }, [isPlaying, onPlayingChange]);

  // iOS 26 Safari heartbeat
  // -----------------------
  // Even with playbackState='playing' set, iOS 26 Safari can revoke the
  // media session ~3-5s after lock unless we keep updating
  // setPositionState. Browsers throttle setInterval to ~1Hz when hidden
  // but that's enough for iOS to keep the session "active" and audio
  // playing through screen lock.
  useEffect(() => {
    if (!track?.id || !('mediaSession' in navigator)) return;
    const interval = setInterval(() => {
      const audio = audioRef.current;
      if (!audio || !audio.duration || isNaN(audio.duration)) return;
      try {
        navigator.mediaSession.setPositionState({
          duration: audio.duration,
          playbackRate: audio.playbackRate || 1,
          position: audio.currentTime,
        });
        // Re-assert playing — some iOS 26 builds clear the state
        // after a few seconds hidden.
        if (!audio.paused) {
          navigator.mediaSession.playbackState = 'playing';
        }
      } catch { /* ignore */ }
    }, 1000);
    return () => clearInterval(interval);
  }, [track?.id]);

  // Handle toggle play from parent (when same track is clicked again)
  useEffect(() => {
    if (track?.togglePlay && track.id === prevTrackIdRef.current && audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
        setIsPlaying(false);
      } else {
        audioRef.current.play().catch(e => console.error('Play error:', e));
        setIsPlaying(true);
      }
    }
    prevTrackIdRef.current = track?.id;
  }, [track]);

  // Reset transport state when the active track changes. Auto-play is
  // triggered separately, AFTER audioSrc is resolved (see effect below) so
  // we never call .play() with an empty/stale src.
  useEffect(() => {
    setCurrentTime(0);
    setDuration(0);
    setIsLoaded(false);
    if (track?.id) {
      setIsPlaying(true);
    }
  }, [track?.id]);

  // Once the presigned R2 URL has been written to the <audio> element,
  // kick off playback. We deliberately do NOT use a setTimeout here — iOS
  // Safari grants background-audio rights only when play() is called
  // synchronously in the same task as a user gesture. A delayed play()
  // makes iOS treat the playback as "incidental" and pause it the moment
  // the screen locks.
  // Once the audio src has been written to the element (by PlayerContext
  // imperatively, or by the restoration effect above), seek to the saved
  // position from localStorage. We do NOT call play() here — PlayerContext
  // already did that synchronously inside the user-gesture chain.
  useEffect(() => {
    if (!audioSrc || !audioRef.current) return;
    const a = audioRef.current;
    try {
      const saved = readPlayerState();
      if (saved && saved.trackId === track?.id && saved.currentTime > 1) {
        a.currentTime = saved.currentTime;
      }
    } catch { /* ignore */ }
  }, [audioSrc, track?.id]);

  // Persist the active track + position to localStorage so the player can
  // auto-restore after a hard reload (iOS Safari kills tabs aggressively
  // when the screen is locked for a long time). Throttled to once every
  // ~3 seconds to avoid hammering localStorage.
  useEffect(() => {
    if (!track?.id) return;
    writePlayerState({ trackId: track.id, currentTime: 0, closed: false });
  }, [track?.id]);

  useEffect(() => {
    if (!track?.id) return;
    const interval = setInterval(() => {
      if (audioRef.current && !audioRef.current.paused) {
        writePlayerState({
          trackId: track.id,
          currentTime: audioRef.current.currentTime,
          closed: false,
        });
      }
    }, 3000);
    return () => clearInterval(interval);
  }, [track?.id]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => {
      if (!isSeeking) {
        setCurrentTime(audio.currentTime);
      }
    };
    const handleLoadedMetadata = () => {
      setDuration(audio.duration);
      setIsLoaded(true);
    };
    const handleEnded = () => {
      setIsPlaying(false);
      // Auto-play next track if available. Use refs (not closure values)
      // so the latest queue / index is used — without this, shuffle and
      // queue updates wouldn't carry over to auto-advance because the
      // event listener was registered with stale tracks/currentIndex.
      if (hasNextRef.current && onTrackChangeRef.current) {
        const nextTrack = tracksRef.current[currentIndexRef.current + 1];
        if (nextTrack) onTrackChangeRef.current(nextTrack);
      }
    };
    const handleCanPlay = () => {
      setIsLoaded(true);
      // Prefetch the NEXT queue item's audio URL as soon as the current
      // track is playable. This is the piece that makes auto-advance
      // work on iOS Safari — see PlayerContext.handleTrackChange fast path.
      const next = tracksRef.current[currentIndexRef.current + 1];
      if (next && next.id && prefetchNextAudioUrl) {
        prefetchNextAudioUrl(next.id);
      }
    };
    const handleSeeked = () => setIsSeeking(false);
    // Sync React state whenever the <audio> element starts/stops playing —
    // covers OS-level interruptions (phone calls, other apps taking audio
    // focus, headphones unplugged, screen lock on iOS, route changes, etc.)
    const handlePlay = () => setIsPlaying(true);
    const handlePause = () => setIsPlaying(false);

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);
    audio.addEventListener('canplay', handleCanPlay);
    audio.addEventListener('seeked', handleSeeked);
    audio.addEventListener('play', handlePlay);
    audio.addEventListener('pause', handlePause);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
      audio.removeEventListener('canplay', handleCanPlay);
      audio.removeEventListener('seeked', handleSeeked);
      audio.removeEventListener('play', handlePlay);
      audio.removeEventListener('pause', handlePause);
    };
  }, [track?.id, isSeeking, hasNext, currentIndex, tracks, onTrackChange]);

  useEffect(() => {
    if (audioRef.current) {
      audioRef.current.volume = isMuted ? 0 : volume * duckFactor;
    }
  }, [volume, isMuted, duckFactor]);

  const togglePlay = () => {
    if (audioRef.current) {
      if (isPlaying) {
        audioRef.current.pause();
      } else {
        audioRef.current.play().catch(e => console.error('Play error:', e));
      }
      setIsPlaying(!isPlaying);
    }
  };

  const playNext = () => {
    if (hasNext && onTrackChange) {
      onTrackChange(tracks[currentIndex + 1]);
    }
  };

  const playPrev = () => {
    if (hasPrev && onTrackChange) {
      onTrackChange(tracks[currentIndex - 1]);
    }
  };

  const skipTime = (seconds) => {
    if (audioRef.current && isLoaded) {
      const newTime = Math.max(0, Math.min(duration, audioRef.current.currentTime + seconds));
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const handleProgressClick = (e) => {
    if (!progressRef.current || !audioRef.current || !isLoaded) return;
    
    e.preventDefault();
    e.stopPropagation();
    
    const rect = progressRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percent = Math.max(0, Math.min(1, clickX / rect.width));
    const newTime = percent * duration;
    
    setIsSeeking(true);
    setCurrentTime(newTime);
    audioRef.current.currentTime = newTime;
  };

  const handleProgressDrag = (e) => {
    if (!progressRef.current || !audioRef.current || !isLoaded) return;
    
    const rect = progressRef.current.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const percent = Math.max(0, Math.min(1, clickX / rect.width));
    const newTime = percent * duration;
    
    setCurrentTime(newTime);
    audioRef.current.currentTime = newTime;
  };

  const skip = (seconds) => {
    if (audioRef.current && isLoaded) {
      const newTime = Math.max(0, Math.min(duration, audioRef.current.currentTime + seconds));
      audioRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  const formatTime = (time) => {
    if (!time || isNaN(time)) return '0:00';
    const mins = Math.floor(time / 60);
    const secs = Math.floor(time % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const progress = duration > 0 ? (currentTime / duration) * 100 : 0;
  const analysis = track?.analysis || {};

  // Network-aware prefetch strategy
  // - slow-2g / 2g or saveData=true  → prefetch 0 (skip entirely)
  // - 3g                             → prefetch 1 next track
  // - 4g / wi-fi / unknown           → prefetch next 2 tracks
  // navigator.connection is supported on Chromium/Android; Safari falls
  // through to the default (2 tracks) which matches typical iOS Wi-Fi usage.
  const prefetchCount = useMemo(() => {
    if (typeof navigator === 'undefined') return 2;
    const c = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
    if (!c) return 2;
    if (c.saveData) return 0;
    const t = c.effectiveType;
    if (t === 'slow-2g' || t === '2g') return 0;
    if (t === '3g') return 1;
    return 2; // '4g' or undefined
  }, []);

  const prefetchTracks = useMemo(() => {
    if (prefetchCount === 0 || !hasNext) return [];
    const slice = tracks.slice(currentIndex + 1, currentIndex + 1 + prefetchCount);
    return slice.filter(t => t && (t.has_audio || t.storage_path));
  }, [tracks, currentIndex, hasNext, prefetchCount]);

  // We ALWAYS render the same single <audio> element, regardless of
  // whether a track is active or not. This is critical: switching
  // between two different <audio> elements (hidden vs visible) caused
  // the audioElRef registered with PlayerContext to point at a stale
  // unmounted node by the time the JSON fetch completed, so the new
  // src+play() ran on a detached element. Single element = stable ref.
  const audioElement = (
    <audio
      ref={audioRef}
      preload="metadata"
      playsInline
      data-cratelab-music
    />
  );

  if (!track) {
    return (
      <div style={{ position: 'fixed', width: 0, height: 0, overflow: 'hidden' }} aria-hidden="true">
        {audioElement}
      </div>
    );
  }

  return (
    <div className="fixed bottom-0 left-0 right-0 bg-white border-t border-[#E2E8F0] p-4 z-50">
      {audioElement}

      {/* Prefetch is disabled when the /audio endpoint returns JSON
          instead of audio bytes — using <audio> elements as a prefetch
          warmer no longer works. Browser-level HTTP caching of the R2
          URL still happens once the user actually plays the next track. */}
      
      <div className="max-w-6xl mx-auto">
        <div className="flex items-center gap-6">
          {/* Track info */}
          <div className="flex-shrink-0 flex items-center gap-3 w-56 min-w-0">
            <div className="w-11 h-11 rounded-md overflow-hidden border border-[#E2E8F0] shrink-0 bg-[#F8FAFC]">
              {track?.cover_url ? (
                <SafeImg
                  src={track.cover_url}
                  alt=""
                  className="w-full h-full object-cover"
                  iconClassName="w-4 h-4 text-[#94A3B8]"
                />
              ) : (
                <div className="w-full h-full flex items-center justify-center bg-gradient-to-br from-[#EFF6FF] to-[#ECFEFF]">
                  <Music className="w-4 h-4 text-[#94A3B8]" />
                </div>
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-heading text-[#0F172A] text-sm truncate">{track?.title}</p>
              <p className="font-body text-[#64748B] text-xs truncate">{track?.artist}</p>
            </div>
          </div>

          {/* Controls */}
          <div className="flex items-center gap-2">
            {/* Previous track */}
            <button
              onClick={playPrev}
              disabled={!hasPrev}
              className={`p-2 transition-colors ${hasPrev ? 'text-[#64748B] hover:text-[#0F172A]' : 'text-[#475569] cursor-not-allowed'}`}
              title="Previous track"
            >
              <SkipBack className="w-5 h-5" />
            </button>

            {/* Play/Pause */}
            <button
              onClick={togglePlay}
              className="p-3 bg-[#2563FF] text-[#0F172A] rounded-full hover:bg-[#2563FF] transition-colors"
              data-testid="player-play-btn"
            >
              {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5 ml-0.5" />}
            </button>

            {/* Next track */}
            <button
              onClick={playNext}
              disabled={!hasNext}
              className={`p-2 transition-colors ${hasNext ? 'text-[#64748B] hover:text-[#0F172A]' : 'text-[#475569] cursor-not-allowed'}`}
              title="Next track"
            >
              <SkipForward className="w-5 h-5" />
            </button>
          </div>

          {/* Waveform progress bar */}
          <div className="flex-1 flex items-center gap-3">
            <span className="font-mono text-xs text-[#64748B] w-12 text-right">
              {formatTime(currentTime)}
            </span>

            <div
              ref={progressRef}
              onClick={handleProgressClick}
              onMouseDown={(e) => {
                if (e.button === 0) {
                  handleProgressClick(e);
                  const handleMouseMove = (moveE) => handleProgressDrag(moveE);
                  const handleMouseUp = () => {
                    document.removeEventListener('mousemove', handleMouseMove);
                    document.removeEventListener('mouseup', handleMouseUp);
                  };
                  document.addEventListener('mousemove', handleMouseMove);
                  document.addEventListener('mouseup', handleMouseUp);
                }
              }}
              className={`flex-1 h-14 rounded-md cursor-pointer group relative overflow-hidden bg-[#F8FAFC] border border-[#E2E8F0] hover:border-[#2563FF]/30 transition-colors ${!isLoaded ? 'opacity-50' : ''}`}
              data-testid="audio-progress-bar"
            >
              <WaveformBars
                structure={analysis.structure}
                duration={track?.duration || 1}
                progress={progress}
                trackId={track?.id}
              />

              {/* Playhead */}
              <div
                className="absolute top-0 bottom-0 w-0.5 bg-[#2563FF] pointer-events-none shadow-[0_0_8px_rgba(37,99,255,0.6)]"
                style={{ left: `${progress}%` }}
              />
              <div
                className="absolute top-1/2 -translate-y-1/2 w-3.5 h-3.5 bg-[#2563FF] rounded-full ring-2 ring-white opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none"
                style={{ left: `${progress}%`, transform: 'translate(-50%, -50%)' }}
              />

              {/* Drop markers */}
              {analysis.drops && analysis.drops.map((dropTime, i) => {
                const dropPos = (dropTime / (track?.duration || 1)) * 100;
                return (
                  <div
                    key={`drop-${i}`}
                    className="absolute top-0 bottom-0 w-px bg-[#3B82F6]/70 pointer-events-none"
                    style={{ left: `${dropPos}%` }}
                    title={`Drop at ${formatTime(dropTime)}`}
                  />
                );
              })}

              {/* Cue point markers */}
              {analysis.cue_points && analysis.cue_points.map((cue, i) => {
                const cuePos = (cue.time / (track?.duration || 1)) * 100;
                return (
                  <div
                    key={`cue-${i}`}
                    className="absolute top-0.5 w-2 h-2 bg-amber-400 transform rotate-45 pointer-events-none"
                    style={{ left: `${cuePos}%`, marginLeft: '-4px' }}
                    title={`${cue.label} at ${formatTime(cue.time)}`}
                  />
                );
              })}
            </div>

            <span className="font-mono text-xs text-[#64748B] w-12">
              {formatTime(duration)}
            </span>
          </div>

          {/* Volume */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              onClick={() => setIsMuted(!isMuted)}
              className="p-2 text-[#64748B] hover:text-[#0F172A] transition-colors"
            >
              {isMuted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
            </button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={volume}
              onChange={(e) => setVolume(parseFloat(e.target.value))}
              className="w-20 accent-[#2563FF]"
            />
          </div>

          {/* Close button */}
          <button
            onClick={() => { markPlayerClosed(); if (onClose) onClose(); }}
            className="p-2 text-[#64748B] hover:text-[#0F172A] transition-colors"
          >
            ✕
          </button>
        </div>
      </div>
    </div>
  );
}
